import "../../App.css";
import './css/inputs.css';
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ProSidebar } from "../../components/general/ProSidebar";
import { API_ENDPOINTS } from '../../config/apiConfig';
import { CustomMap } from "../../components/general/CustomMap";
import { Notification } from "../../components/general/Notification";

// Import step components
import RouteSelection from "../../components/pro_calculator/inputs/RouteSelection";
import DepotSelection from "../../components/pro_calculator/inputs/DepotSelection";
import TruckSelection from "../../components/pro_calculator/inputs/TruckSelection";
import EnergyRate from "../../components/pro_calculator/inputs/EnergyRate";
import OtherParameters from "../../components/pro_calculator/inputs/OtherParameters";
import ReviewAndOptimize from "../../components/pro_calculator/inputs/ReviewAndOptimize";
import { parameterGroups, initializeDefaultParameters } from "../../components/pro_calculator/inputs/OtherParameters";
import ChargingStationSelection from "../../components/pro_calculator/inputs/ChargingStationSelection";
import ParameterTable from "../../components/pro_calculator/inputs/common/ParameterTable";

// Helper functions moved outside the component
const getRouteGeometry = (routeId, rawDataSource) => {
  const rawData = rawDataSource[routeId];
  if (!rawData) {
    console.error(`No raw data found for route ${routeId}`);
    return [];
  }
  
  // Check for links property
  if (rawData.links && Array.isArray(rawData.links)) {
    return rawData.links;
  }
  
  // Alternative data structure - check if raw data itself is an array
  if (Array.isArray(rawData)) {
    return rawData;
  }
  
  console.warn(`Unknown format for route ${routeId} data:`, rawData);
  return [];
};

function Inputs() {
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");
  
  // ----------------------------------------
  // State organization by category
  // ----------------------------------------
  
  // Authentication & UI States
  const [authenticated, setAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [notification, setNotification] = useState({
    message: "",
    type: "info",
    visible: false
  });

  // Wizard Step State
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 7;
  
  // Data Loading States
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [loadingDepots, setLoadingDepots] = useState(true);
  const [loadingChargingStations, setLoadingChargingStations] = useState(true);
  const [loadingTrucks, setLoadingTrucks] = useState(true);
  const [loadingCircuits, setLoadingCircuits] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  
  // Data Collections
  const [routes, setRoutes] = useState([]);
  const [depots, setDepots] = useState([]);
  const [chargingStations, setChargingStations] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [routesRawData, setRoutesRawData] = useState({});
  const [circuitCache, setCircuitCache] = useState({});
  
  // Selected Items
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedDepots, setSelectedDepots] = useState([]);
  const [selectedChargingStations, setSelectedChargingStations] = useState([]);
  const [selectedTrucks, setSelectedTrucks] = useState([]);
  const [truckCustomData, setTruckCustomData] = useState({});

  // Optimization Parameters
  const [energyChargeData, setEnergyChargeData] = useState([]);
  const [otherParameters, setOtherParameters] = useState(initializeDefaultParameters(parameterGroups));
  const [optimizationResults, setOptimizationResults] = useState(null);
  
  // Map-related States
  const [showMap, setShowMap] = useState(true);
  const [selectedRoutesMap, setSelectedRoutesMap] = useState({});
  const [selectedDepotsMap, setSelectedDepotsMap] = useState({});
  const [selectedChargersMap, setSelectedChargersMap] = useState({});
  const [coordinates, setCoordinates] = useState([]);
  const [circuitData, setCircuitData] = useState({});
  const [mapRefreshTrigger, setMapRefreshTrigger] = useState(0);
  const [highlightedRoute, setHighlightedRoute] = useState(false);
  const [mapRouteDisplay, setMapRouteDisplay] = useState({});
  const [mapDepotDisplay, setMapDepotDisplay] = useState({});
  const [mapChargerDisplay, setMapChargerDisplay] = useState({});

  // ----------------------------------------
  // Event Handlers
  // ----------------------------------------
  
  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const toggleMapDisplay = () => {
    setShowMap(!showMap);
  };

  // Step navigation
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const shouldShowMap = () => {
    // Only show map for steps 1, 2, and 3
    return currentStep <= 3;
  };

  // Map-related handlers
  const handleRouteSelect = async (routeId, fromMap = false) => {
    // Update the selectedRoutes array based on the map selection
    if (fromMap) {
      if (!selectedRoutes.includes(routeId)) {
        setSelectedRoutes(prev => [...prev, routeId]);
      } else {
        setSelectedRoutes(prev => prev.filter(id => id !== routeId));
      }
    }
  };

  const handleDepotSelect = async (depotId, fromMap = false) => {
    // Update the selectedDepots array based on the map selection
    if (fromMap) {
      if (!selectedDepots.includes(depotId)) {
        setSelectedDepots(prev => [...prev, depotId]);
      } else {
        setSelectedDepots(prev => prev.filter(id => id !== depotId));
      }
    }
    
    // Fetch circuit data for the selected depot if we have circuit names
    const depot = depots.find(d => d.depot_id === depotId);
    if (depot && depot.circuits && depot.circuits.length > 0) {
      // Only fetch if we don't already have the data
      if (!circuitData[depotId]) {
        await fetchCircuitData(depotId, depot.circuits);
      }
    }
  };

  const handleChargerSelect = (chargerId, fromMap = false) => {
    // Update the selectedChargingStations array based on the map selection
    if (fromMap) {
      if (!selectedChargingStations.includes(chargerId)) {
        setSelectedChargingStations(prev => [...prev, chargerId]);
      } else {
        setSelectedChargingStations(prev => prev.filter(id => id !== chargerId));
      }
    }
  };

  // ----------------------------------------
  // Data Fetching Functions
  // ----------------------------------------
  
  const refreshRouteData = () => {
    setLoadingRoutes(true);
    return axios.get(API_ENDPOINTS.getAllRawRoutes, {
      headers: {
        Authorization: `Token ${authToken}`,
      },
    })
    .then((response) => {
      const fetchedRoutes = response.data;
      setRoutes(fetchedRoutes);
      
      // Create a raw data object from the routes
      const routesRawDataObj = {};
      fetchedRoutes.forEach(route => {
        routesRawDataObj[route.id] = route;
      });
      setRoutesRawData(routesRawDataObj);
      
      setLoadingRoutes(false);
      return fetchedRoutes;
    })
    .catch((error) => {
      console.error("Error fetching routes:", error);
      setLoadingRoutes(false);
      throw error;
    });
  };
  
  const refreshDepotData = () => {
    setLoadingDepots(true);
    return axios.get(API_ENDPOINTS.getAllDepots, {
      headers: {
        Authorization: `Token ${authToken}`,
      },
    })
    .then((response) => {
      const fetchedDepots = response.data;
      setDepots(fetchedDepots);
      setLoadingDepots(false);
      return fetchedDepots;
    })
    .catch((error) => {
      console.error("Error fetching depots:", error);
      setFetchError("Failed to load depots. Please try again.");
      setLoadingDepots(false);
      throw error;
    });
  };

  const refreshChargingStationData = () => {
    setLoadingChargingStations(true);
    return axios.get(API_ENDPOINTS.getAllChargingStations, {
      headers: {
        Authorization: `Token ${authToken}`,
      },
    })
    .then((response) => {
      const fetchedChargingStations = response.data;
      setChargingStations(fetchedChargingStations);
      setLoadingChargingStations(false);
      return fetchedChargingStations;
    })
    .catch((error) => {
      console.error("Error fetching charging stations:", error);
      setFetchError("Failed to load charging stations. Please try again.");
      setLoadingChargingStations(false);
      throw error;
    });
  };

  const refreshTruckData = () => {
    setLoadingTrucks(true);
    return axios.get(API_ENDPOINTS.getTruckData, {
      headers: {
        Authorization: `Token ${authToken}`,
      },
    })
    .then((response) => {
      const fetchedTrucks = response.data;
      setTrucks(fetchedTrucks);
      setLoadingTrucks(false);
      return fetchedTrucks;
    })
    .catch((error) => {
      console.error("Error fetching trucks:", error);
      setFetchError("Failed to load trucks. Please try again.");
      setLoadingTrucks(false);
      throw error;
    });
  };

  // Add this new function for fetching circuit data
  const fetchCircuitData = async (depotId, circuitNames) => {
    if (!circuitNames || circuitNames.length === 0) {
      console.warn(`No circuit names provided for depot ${depotId}`);
      return [];
    }
    
    // Check if all requested circuits are already in cache
    const uncachedCircuits = circuitNames.filter(name => !circuitCache[name]);
    
    if (uncachedCircuits.length === 0) {
      // All circuits are already cached, return from cache
      return circuitNames.map(name => circuitCache[name]).filter(Boolean);
    }
    
    try {
      setLoadingCircuits(true);
      
      const response = await axios.post(
        API_ENDPOINTS.getCircuits,
        { circuit_names: circuitNames },
        {
          headers: {
            Authorization: `Token ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200) {
        const circuits = response.data.circuits;
        
        // Update the circuit cache with new data
        const newCircuitCache = { ...circuitCache };
        circuits.forEach(circuit => {
          newCircuitCache[circuit.circuit_name] = circuit;
        });
        setCircuitCache(newCircuitCache);
        
        // Update circuit data for this specific depot
        setCircuitData(prevData => ({
          ...prevData,
          [depotId]: circuits
        }));
        
        return circuits;
      } else {
        console.error("Error fetching circuits:", response);
        return [];
      }
    } catch (error) {
      console.error("Error fetching circuits:", error);
      return [];
    } finally {
      setLoadingCircuits(false);
    }
  };

  // ----------------------------------------
  // Map Utility Functions
  // ----------------------------------------
  
  const updateMapRouteDisplay = (selectedRouteIds, rawRouteData) => {
    console.log("Updating route display with selected routes:", selectedRouteIds);
    
    const routesObj = {};
    selectedRouteIds.forEach(id => {
      const geometry = getRouteGeometry(id, rawRouteData);
      
      // Include the route geometry data for selected routes
      routesObj[id] = {
        selected: true,
        geometry: geometry
      };
    });
    setMapRouteDisplay(routesObj);
  };

  // ----------------------------------------
  // useEffect Hooks
  // ----------------------------------------
  
  // Authentication check
  useEffect(() => {
    const source = axios.CancelToken.source();

    axios
      .get(API_ENDPOINTS.user, {
        headers: {
          Authorization: `Token ${authToken}`,
        },
        cancelToken: source.token,
      })
      .then((response) => {
        setAuthenticated(true);
        setUserData(response.data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.log('Request canceled', error.message);
        } else {
          setAuthenticated(false);
          navigate("/login");
        }
      });
    return () => {
      source.cancel("Component unmounted, request canceled");
    };
  }, [navigate, authToken]);

  // Initial data loading
  useEffect(() => {
    if (authToken) {
      refreshRouteData()
        .then((fetchedRoutes) => {
          // Select all routes by default
          setSelectedRoutes(fetchedRoutes.map(route => route.id));
        });
    }
  }, [authToken]);

  // Fetch depots data only once on component mount
  useEffect(() => {
    if (authToken) {
      setLoadingDepots(true);
      refreshDepotData()
        .then((fetchedDepots) => {
          // Select all depots by default
          setSelectedDepots(fetchedDepots.map(depot => depot.depot_id));
        });
    }
  }, [authToken]);

  // Fetch charging stations data only once on component mount
  useEffect(() => {
    if (authToken) {
      setLoadingChargingStations(true);
      refreshChargingStationData()
        .then((fetchedChargingStations) => {
          // Select all charging stations by default if needed
          // If you want consistent behavior with depots and routes
          setSelectedChargingStations(fetchedChargingStations.map(station => station.cs_id));
        });
    }
  }, [authToken]);

  // Fetch truck data only once on component mount
  useEffect(() => {
    if (authToken) {
      setLoadingTrucks(true);
      refreshTruckData()
        .then((fetchedTrucks) => {
          // Select all trucks by default
          setSelectedTrucks(fetchedTrucks.map(truck => truck.truck_id));
        });
    }
  }, [authToken]);

  // Convert array selections to object format for the map
  useEffect(() => {
    // Convert selectedRoutes array to object format for map
    const routesObj = {};
    selectedRoutes.forEach(id => {
      routesObj[id] = true;
    });
    setSelectedRoutesMap(routesObj);

    // Convert selectedDepots array to object format for map
    const depotsObj = {};
    selectedDepots.forEach(id => {
      depotsObj[id] = true;
    });
    setSelectedDepotsMap(depotsObj);

    // Convert selectedChargingStations array to object format for map
    const chargersObj = {};
    selectedChargingStations.forEach(id => {
      chargersObj[id] = true;
    });
    setSelectedChargersMap(chargersObj);
  }, [selectedRoutes, selectedDepots, selectedChargingStations]);

  // Update map display when routes change
  useEffect(() => {
    if (Object.keys(routesRawData).length > 0) {
      updateMapRouteDisplay(selectedRoutes, routesRawData);
    }
  }, [selectedRoutes, routesRawData]);

  // Update for depots display
  useEffect(() => {
    const depotsObj = {};
    selectedDepots.forEach(id => {
      depotsObj[id] = true;
    });
    setMapDepotDisplay(depotsObj);
  }, [selectedDepots]);

  // Update for charging stations display
  useEffect(() => {
    const chargersObj = {};
    selectedChargingStations.forEach(id => {
      chargersObj[id] = true;
    });
    setMapChargerDisplay(chargersObj);
  }, [selectedChargingStations]);

  // After fetching depots, pre-fetch all circuit data for faster UI
  useEffect(() => {
    if (depots.length > 0 && authToken) {
      const getAllCircuitNames = () => {
        const allCircuitNames = new Set();
        depots.forEach(depot => {
          if (depot.circuits && Array.isArray(depot.circuits)) {
            depot.circuits.forEach(circuitName => {
              allCircuitNames.add(circuitName);
            });
          }
        });
        return Array.from(allCircuitNames);
      };
      
      const allCircuitNames = getAllCircuitNames();
      
      if (allCircuitNames.length > 0) {
        // Only fetch circuits that aren't already cached
        const uncachedCircuits = allCircuitNames.filter(name => !circuitCache[name]);
        
        if (uncachedCircuits.length > 0) {
          setLoadingCircuits(true);
          axios.post(
            API_ENDPOINTS.getCircuits,
            { circuit_names: uncachedCircuits },
            {
              headers: {
                Authorization: `Token ${authToken}`,
                'Content-Type': 'application/json',
              },
            }
          )
          .then(response => {
            if (response.status === 200) {
              const circuits = response.data.circuits;
              
              // Update the circuit cache
              const newCircuitCache = { ...circuitCache };
              circuits.forEach(circuit => {
                newCircuitCache[circuit.circuit_name] = circuit;
              });
              setCircuitCache(newCircuitCache);
              
              // Pre-populate circuit data for each depot
              const newCircuitData = { ...circuitData };
              depots.forEach(depot => {
                if (depot.circuits && Array.isArray(depot.circuits) && depot.circuits.length > 0) {
                  newCircuitData[depot.depot_id] = depot.circuits
                    .map(name => newCircuitCache[name])
                    .filter(Boolean);
                }
              });
              setCircuitData(newCircuitData);
            }
          })
          .catch(error => {
            console.error("Error pre-fetching circuits:", error);
          })
          .finally(() => {
            setLoadingCircuits(false);
          });
        }
      }
    }
  }, [depots, authToken, circuitCache]);

  // Add this effect to initialize truck custom data when trucks are loaded
  useEffect(() => {
    if (trucks.length > 0) {
      const initialCustomData = {};
      
      trucks.forEach(truck => {
        initialCustomData[truck.truck_id] = {
          price: truck.price,
          incentives: truck.incentives
        };
      });
      
      setTruckCustomData(initialCustomData);
    }
  }, [trucks]);

  // ----------------------------------------
  // Step Rendering Function
  // ----------------------------------------
  
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <RouteSelection
            routes={routes}
            loading={loadingRoutes}
            selectedRoutes={selectedRoutes}
            setSelectedRoutes={setSelectedRoutes}
            nextStep={nextStep}
            onEditRoute={(formData, wasSuccessful, operation = "edit") => {
              // This handles adding, editing, and deleting routes
              if (wasSuccessful) {
                setLoadingOverlay(true);
                
                // Use the reusable function instead of duplicating the API call
                refreshRouteData()
                  .then((fetchedRoutes) => {
                    // Update selected routes if needed
                    // After deletion, remove deleted routes from selection
                    if (selectedRoutes.some(id => !fetchedRoutes.find(r => r.id === id))) {
                      setSelectedRoutes(selectedRoutes.filter(id => 
                        fetchedRoutes.find(r => r.id === id)
                      ));
                    }
                    
                    // If it was an add operation and the form data has an id, add it to selected
                    if (formData.route_id && !selectedRoutes.includes(formData.route_id)) {
                      setSelectedRoutes(prev => [...prev, formData.route_id]);
                    }
                    
                    setLoadingOverlay(false);
                    
                    // Update the map display
                    updateMapRouteDisplay(selectedRoutes, routesRawData);
                    
                    // Show appropriate notification based on operation type
                    let message = "Route updated successfully!";
                    if (operation === "add") {
                      message = "Route added successfully!";
                    } else if (operation === "delete") {
                      message = `${selectedRoutes.length > 1 ? 'Routes' : 'Route'} deleted successfully!`;
                    }
                    
                    setNotification({
                      message: message,
                      type: "success",
                      visible: true
                    });
                    
                    // Hide notification after 3 seconds
                    setTimeout(() => {
                      setNotification(prev => ({...prev, visible: false}));
                    }, 5000);
                  })
                  .catch(() => {
                    setLoadingOverlay(false);
                  });
              } else {
                // Handle error case - show error notification
                const errorMessage = formData.errorMessage || "Operation failed. Please try again.";
                
                setNotification({
                  message: errorMessage,
                  type: "error",
                  visible: true
                });
                
                // Hide notification after 5 seconds
                setTimeout(() => {
                  setNotification(prev => ({...prev, visible: false}));
                }, 10000);
              }
            }}
          />
        );
      case 2:
        return (
          <DepotSelection
            depots={depots}
            loading={loadingDepots}
            error={fetchError}
            selectedDepots={selectedDepots}
            setSelectedDepots={setSelectedDepots}
            nextStep={nextStep}
            prevStep={prevStep}
            refreshDepots={refreshDepotData}
            loadingCircuits={loadingCircuits}
          />
        );
      case 3:
        return (
          <ChargingStationSelection
            chargingStations={chargingStations}
            loading={loadingChargingStations}
            error={fetchError}
            selectedChargingStations={selectedChargingStations}
            setSelectedChargingStations={setSelectedChargingStations}
            nextStep={nextStep}
            prevStep={prevStep}
            refreshChargingStations={refreshChargingStationData}
          />
        );
      case 4:
        return (
          <TruckSelection
            trucks={trucks}
            loading={loadingTrucks}
            selectedTrucks={selectedTrucks}
            setSelectedTrucks={setSelectedTrucks}
            truckCustomData={truckCustomData}
            setTruckCustomData={setTruckCustomData}
            otherParameters={otherParameters}
            setOtherParameters={setOtherParameters}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 5:
        return (
          <EnergyRate
            energyChargeData={energyChargeData}
            setEnergyChargeData={setEnergyChargeData}
            otherParameters={otherParameters}
            setOtherParameters={setOtherParameters}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 6:
        return (
          <OtherParameters
            otherParameters={otherParameters}
            setOtherParameters={setOtherParameters}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 7:
        return (
          <ReviewAndOptimize
            selectedRoutes={selectedRoutes}
            selectedDepots={selectedDepots}
            selectedChargingStations={selectedChargingStations}
            selectedTrucks={selectedTrucks}
            truckCustomData={truckCustomData}
            routes={routes}
            depots={depots}
            chargingStations={chargingStations}
            trucks={trucks}
            energyChargeData={energyChargeData}
            otherParameters={otherParameters}
            prevStep={prevStep}
          />
        );
      default:
        return null;
    }
  };

  // ----------------------------------------
  // Step Title Helper Function
  // ----------------------------------------
  
  const renderStepTitle = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <h2 className="step-title">Step 1: Route Selection</h2>
            <p className="step-description">Select the routes you would like to include in your analysis. Each route will be assigned to a single truck. We will help you assess whether it is better served by a diesel or electric vehicle, recommend the most suitable electric truck model if applicable, and suggest the optimal charging strategy.</p>
          </>
        );
      case 2:
        return (
          <>
            <h2 className="step-title">Step 2: Depot Selection</h2>
            <p className="step-description">Do you have a depot where your vehicles are parked and chargers could be installed? Add it here to assess how many chargers you will need and explore the potential benefits of integrating solar and battery storage. No need to worry about utility power constraints — we will help you navigate those.</p>
          </>
        );
      case 3:
        return (
          <>
            <h2 className="step-title">Step 3: Public Charger Selection</h2>
            <p className="step-description">We provide a list of available third-party commercial charging stations for medium- and heavy-duty trucks. You can select which stations to include in your optimization—filter by status, ownership, or other criteria to tailor the analysis to your needs.</p>
          </>
        );
      case 4:
        return (
          <>
            <h2 className="step-title">Step 4: Truck Configuration</h2>
            <p className="step-description">Select the electric truck models you are interested in, and adjust the MSRP and available incentives as needed. You can also configure diesel truck parameters and define operational lifespans to support a comprehensive analysis.</p>
          </>
        );
      case 5:
        return (
          <>
            <h2 className="step-title">Step 5: Energy Rate Settings</h2>
            <p className="step-description">Set your energy rates here for both depot and public charging, along with diesel prices. Our model will systematically evaluate operational costs for both electric and diesel vehicles. </p>
          </>
        );
      case 6:
        return (
          <>
            <h2 className="step-title">Step 6: Advanced Parameters</h2>
            <p className="step-description">Here are a few more parameters that impact your operational costs and warrant your attention. Adjust them to conduct scenario analysis.</p>
          </>
        );
      case 7:
        return (
          <>
            <h2 className="step-title">Step 7: Review and Optimize</h2>
            <p className="step-description">Finally, review your case settings. If everything looks good, click the Run Optimization button below, and our optimization engine will handle the rest.</p>
          </>
        );
      default:
        return null;
    }
  };

  // ----------------------------------------
  // Component Render
  // ----------------------------------------
  
  return (
    <div className="container-flex-row">
      <ProSidebar collapsed={collapsed} toggleCollapse={toggleCollapse} />
      <div className="container-flex-col sidebar-adjustment">
        {/* Step Navigation - moved outside of dashboard container */}
        <div className="step-progress">
          <button 
            className="nav-btn prev-btn"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            Back
          </button>
          
          <div className="step-indicators-container">
            {[1, 2, 3, 4, 5, 6, 7].map(step => (
              <div
                key={step}
                className={`step-indicator ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
                onClick={() => setCurrentStep(step)}
              >
                <div className="step-number">{step}</div>
                <div className="step-label">
                  {step === 1 && "Routes"}
                  {step === 2 && "Depots"}
                  {step === 3 && "Public Chargers"}
                  {step === 4 && "Truck Config"}
                  {step === 5 && "Energy Rates"}
                  {step === 6 && "Parameters"}
                  {step === 7 && "Review"}
                </div>
              </div>
            ))}
          </div>
          
          <button 
            className="nav-btn next-btn"
            onClick={nextStep}
            disabled={currentStep === totalSteps}
          >
            Next
          </button>
        </div>
        
        {/* Title container - moved outside of dashboard container */}
        <div className="title-container">
          {/* Step title and description section */}
          <div className="step-title-container">
            {renderStepTitle()}
          </div>

          {/* Map toggle switch - only show when map should be visible */}
          {shouldShowMap() && (
            <label className="toggle-switch">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span className="switch-label">Map</span>
                <input 
                  type="checkbox" 
                  checked={showMap}
                  onChange={toggleMapDisplay}
                />
                <span className="slider"></span>
              </div>
            </label>
          )}
        </div>

        {/* Map display - only show for steps 1-3 and if showMap is true */}
        {shouldShowMap() && showMap && (
          <div className="map-dashboard-container map-container-top">
            <CustomMap
              selectedRoutes={mapRouteDisplay}
              selectedEntities={mapDepotDisplay}
              selectedChargers={mapChargerDisplay}
              circuitData={circuitData}
              onMarkerClick={handleDepotSelect}
              onChargerClick={handleChargerSelect}
              onRouteClick={handleRouteSelect}
              highlightedRoute={highlightedRoute}
              depotList={depots}
              chargerList={chargingStations}
              refreshTrigger={mapRefreshTrigger}
              loadingCircuits={loadingCircuits}
            />
          </div>
        )}

        <div className="wizard-container">
          {renderCurrentStep()}
        </div>

        {/* Notification component */}
        <Notification 
          message={notification.message}
          type={notification.type}
          visible={notification.visible}
        />
      </div>
    </div>
  );
}

export default Inputs;
